import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, effect, inject, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserRole } from '@klubovna/api-client';
import {
  AuthService,
  BaseScreenComponent,
  ButtonComponent,
  homeRouteForRole,
  InputComponent,
  LoginForm,
  SharedModule,
} from '@shared';
import { UserFacade } from '@store/user/user.facade';

@Component({
  selector: 'app-login',
  imports: [ ReactiveFormsModule, InputComponent, ButtonComponent, SharedModule ],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent extends BaseScreenComponent implements AfterViewInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = new FormBuilder();
  private readonly authService = inject(AuthService);
  readonly userFacade = inject(UserFacade);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private returnUrl: string | null = null;

  loginForm: FormGroup<LoginForm> = this.fb.nonNullable.group({
    email: [ '', [ Validators.required, Validators.email ] ],
    password: [ '', [ Validators.required ] ],
  });

  emailErrorMessages = {
    required: 'email_required',
    email: 'email_invalid_format',
  };

  passwordErrorMessages = {
    required: 'password_required',
  };

  constructor() {
    super();
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    this.checkAndRedirectIfLoggedIn();

    effect(() => {
      if (this.userFacade.isAuthenticated() && this.userFacade.user()) {
        this.redirectUserBasedOnRole(this.userFacade.userRole());
      }
    });
  }

  private checkAndRedirectIfLoggedIn(): void {
    if (!this.isBrowser) return;

    const storedUser = this.authService.getUserFromStorage();
    if (storedUser) {
      if (this.returnUrl) {
        this.router.navigateByUrl(this.returnUrl);
      } else {
        this.redirectUserBasedOnRole(storedUser.role, storedUser.additionalRoles);
      }
    }
  }

  ngAfterViewInit() {
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      const credentials = {
        email: this.loginForm.value.email!,
        password: this.loginForm.value.password!,
      };

      this.userFacade.login(credentials);
    } else {
      this.loginForm.markAllAsTouched();
    }
  }

  navigateToResetPassword(): void {
    this.router.navigate([ '/reset-password' ]);
  }

  private redirectUserBasedOnRole(role?: UserRole, additionalRoles: UserRole[] = this.userFacade.additionalRoles()): void {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }

    this.router.navigateByUrl(homeRouteForRole(role || this.userFacade.userRole(), additionalRoles));
  }
}
